import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Bike, CarTaxiFront, CloudDownload, Save, Send, Zap } from 'lucide-react';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { PageHeader } from '../../components/common/PageHeader';
import { Panel } from '../../components/common/Panel';
import { NativeSelect } from '../../components/common/NativeSelect';
import { usePrototype } from '../../contexts/PrototypeContext';
import { trafficDivisions } from '../../data/cngs';
import { vehicleTypeByLabel, vehicleTypes } from '../../data/vehicleTypes';
import type { VehicleTypeKey } from '../../data/vehicleTypes';
interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  mono?: boolean;
  error?: string;
  hint?: string;
}
const typeIcons: Record<VehicleTypeKey, typeof Bike> = {
  cng: CarTaxiFront,
  auto: Zap,
  motorcycle: Bike
};
const samples: Record<VehicleTypeKey, Record<string, string>> = {
  cng: {
    chassis: 'MDH12F45ABP047218',
    engine: '',
    model: 'বাজাজ RE 4S (CNG)',
    color: 'সবুজ',
    year: '২০২৩',
    regDate: '১৪ ফেব্রুয়ারি ২০২৪',
    fitnessExpiry: '১৩ ফেব্রুয়ারি ২০২৮',
    extra1: 'CYL-RNG-58214',
    extra2: '২০ জানুয়ারি ২০২৭',
    extra3: 'ঢাকা মহানগর'
  },
  auto: {
    chassis: 'EZB-LX-2024-77812',
    engine: '',
    model: 'ইজিবাইক ৫-সিটার (লেড-এসিড)',
    color: 'নীল-সাদা',
    year: '২০২৪',
    regDate: '০৩ মার্চ ২০২৪',
    fitnessExpiry: '৩০ জুন ২০২৬',
    extra1: '৫টি × ১২V ১২০Ah',
    extra2: '১,০০০ ওয়াট BLDC',
    extra3: 'মিরপুর-কল্যাণপুর-গাবতলী'
  },
  motorcycle: {
    chassis: 'MD2A11CZ6PCH45612',
    engine: '',
    model: 'বাজাজ পালসার N160',
    color: 'কালো',
    year: '২০২৩',
    regDate: '১৮ সেপ্টেম্বর ২০২৩',
    fitnessExpiry: '',
    extra1: '১৬০ সিসি',
    extra2: 'INS-GRN-2026-118745',
    extra3: 'পাঠাও'
  }
};
function Field({
  id,
  label,
  value,
  onChange,
  required,
  mono,
  error,
  hint
}: FieldProps) {
  return <div data-id="element-1215" data-mp="3sf0c02d4np3">
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-slate-600" data-id="element-1216" data-mp="3s54vwyjtoc0">
        {label} {required && <span className="text-red-600" data-id="element-1217" data-mp="1vw162hptuov8">*</span>}
      </label>
      <Input id={id} value={value} onChange={e => onChange(e.target.value)} className={`${mono ? 'font-mono' : ''} ${error ? 'border-red-400' : ''}`} aria-invalid={Boolean(error)} data-id="element-1218" data-mp="1vxm08688c5sk" />
      {error ? <p className="mt-1 text-[11px] text-red-600" data-id="element-1219" data-mp="24v629hdx2648">{error}</p> : hint && <p className="mt-1 text-[11px] text-slate-400" data-id="element-1220" data-mp="2jl3t6538d9x4">{hint}</p>}
    </div>;
}
export function CngForm() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    getCng,
    setStage
  } = usePrototype();
  const existing = id ? getCng(id) : undefined;
  const editing = Boolean(existing);
  const initialType = existing ? vehicleTypeByLabel(existing.type).key : 'cng';
  const buildState = (type: VehicleTypeKey) => ({
    reg: existing?.reg ?? vehicleTypes.find(v => v.key === type)!.regSample,
    ...samples[type],
    chassis: existing?.chassis ?? samples[type].chassis,
    engine: existing?.engine ?? '',
    model: existing?.model ?? samples[type].model,
    color: existing?.color ?? samples[type].color,
    regDate: existing?.brtaRegDate ?? samples[type].regDate,
    fitnessExpiry: existing?.fitnessExpiry ?? samples[type].fitnessExpiry,
    ownerName: existing?.owner.name ?? 'মোঃ আব্দুল আউয়াল',
    nid: existing?.owner.nid ?? '1983456712390',
    mobile: existing?.owner.mobile ?? '01718-654321',
    address: existing?.owner.address ?? 'বাড়ি ১৮, রোড ২, কল্যাণপুর, মিরপুর, ঢাকা-১২০৭'
  });
  const [type, setType] = useState<VehicleTypeKey>(initialType);
  const [f, setF] = useState(() => buildState(initialType));
  const [ownerDrives, setOwnerDrives] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const cfg = vehicleTypes.find(v => v.key === type)!;
  const set = (k: keyof typeof f) => (v: string) => setF(s => ({
    ...s,
    [k]: v
  }));
  const changeType = (t: VehicleTypeKey) => {
    if (editing) return;
    setType(t);
    setF(buildState(t));
    setErrors({});
  };
  const submit = () => {
    const e: Record<string, string> = {};
    if (!f.reg) e.reg = 'রেজিস্ট্রেশন / লাইসেন্স নং আবশ্যক';
    if (!f.engine) e.engine = `${cfg.engineLabel} আবশ্যক`;
    if (!/^\d{10,17}$/.test(f.nid)) e.nid = 'সঠিক NID প্রদান করুন (১০/১৩/১৭ সংখ্যা)';
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error('কিছু আবশ্যক তথ্য অসম্পূর্ণ');
      return;
    }
    if (existing) setStage(existing.id, Math.max(existing.stage, 1));
    toast.success(`${cfg.short} ${f.reg} যাচাইয়ের জন্য পাঠানো হয়েছে`);
    navigate(existing ? `/admin/cng/${existing.id}` : '/admin/cng');
  };
  const fetchSource = () => {
    setF(s => ({
      ...s,
      engine: s.engine || (type === 'auto' ? 'MTR-BLDC-1000-55821' : type === 'motorcycle' ? 'DHZWPC45612' : 'ENG445566778')
    }));
    toast.info(`${cfg.authority} রেকর্ড থেকে তথ্য পূরণ করা হয়েছে`);
  };
  return <div data-id="element-1221" data-mp="2g6eyuxp3od04">
      <PageHeader title={editing ? `${cfg.short} তথ্য সম্পাদনা — ${existing?.reg}` : 'নতুন গাড়ির তথ্য নিবন্ধন'} subtitle="সিএনজি, ব্যাটারিচালিত অটোরিকশা ও মোটরসাইকেল — তারকা (*) চিহ্নিত ঘর আবশ্যক।" actions={<Button variant="outline" onClick={fetchSource} data-id="element-1223" data-mp="10os2fekswhmu"><CloudDownload className="h-4 w-4" data-id="element-1224" data-mp="14plu8j1d6gl0" />{cfg.authority} থেকে তথ্য আনুন</Button>} data-id="element-1222" data-mp="2g6opmnhyyo6v" />

      <Panel title="যানবাহনের ধরন নির্বাচন করুন" className="mb-4" data-id="element-1225" data-mp="19mkm0uxk2bg1">
        <div className="grid gap-3 md:grid-cols-3" role="radiogroup" aria-label="যানবাহনের ধরন" data-id="element-1226" data-mp="cxcw7apwlg6b">
          {vehicleTypes.map(v => {
          const Icon = typeIcons[v.key];
          const active = v.key === type;
          return <button key={v.key} role="radio" aria-checked={active} disabled={editing && !active} onClick={() => changeType(v.key)} className={`flex items-center gap-3 rounded-lg border p-4 text-left transition ${active ? 'border-navy-700 bg-navy-50 ring-2 ring-navy-100' : 'border-border bg-white hover:border-navy-300'} disabled:cursor-not-allowed disabled:opacity-40`} data-id="element-1227" data-mp="lx9jb7wna6z9">
                <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md ${active ? 'bg-navy-800 text-white' : 'bg-slate-100 text-slate-500'}`} data-id="element-1228" data-mp="wj8aw5iosynl"><Icon className="h-6 w-6" strokeWidth={1.8} data-id="element-1229" data-mp="1adiq9w8snggz" /></span>
                <div className="min-w-0 flex-1" data-id="element-1230" data-mp="16ameopho8kmj">
                  <p className="font-semibold text-navy-900" data-id="element-1231" data-mp="ik8iqnvyu1mo">{v.label}</p>
                  <p className="text-xs text-slate-500" data-id="element-1232" data-mp="imczi6eyqv4d">উৎস: {v.authority} • QR প্রিফিক্স <span className="font-mono" data-id="element-1233" data-mp="dn00knathxlf">{v.qrPrefix}</span></p>
                </div>
              </button>;
        })}
        </div>
      </Panel>

      <div className="grid gap-4 xl:grid-cols-[1fr_300px]" data-id="element-1234" data-mp="mlloyz2z06u4">
        <div className="space-y-4" data-id="element-1235" data-mp="mogcizha67rr">
          <Panel title="মৌলিক তথ্য" subtitle={`Basic Information — ${cfg.en}`} data-id="element-1236" data-mp="22ztztwedorpx">
            <div className="grid gap-4 md:grid-cols-3" data-id="element-1237" data-mp="1y1it3ujdqxbx">
              <Field id="f-reg" label={type === 'auto' ? 'লাইসেন্স / নিবন্ধন নং' : 'রেজিস্ট্রেশন নং'} value={f.reg} onChange={set('reg')} required error={errors.reg} hint={cfg.regHint} data-id="element-1238" data-mp="1y0qzgp9cf9oa" />
              <NativeSelect key={cfg.key} id="f-type" label="যানের ধরন" options={[cfg.label]} defaultValue={cfg.label} disabled data-id="element-1239" data-mp="2aknueonb6x06" />
              <Field id="f-ch" label="চেসিস নং" value={f.chassis} onChange={set('chassis')} required mono data-id="element-1240" data-mp="1wm7sdb0e5uod" />
              <Field id="f-en" label={cfg.engineLabel} value={f.engine} onChange={set('engine')} required mono error={errors.engine} data-id="element-1241" data-mp="21l12ctggz3js" />
              <Field id="f-model" label={type === 'motorcycle' ? 'ব্র্যান্ড ও মডেল' : 'মডেল'} value={f.model} onChange={set('model')} data-id="element-1242" data-mp="2u8514goyyln4" />
              <Field id="f-color" label="রং" value={f.color} onChange={set('color')} data-id="element-1243" data-mp="2fifvo99ufvh1" />
              <Field id="f-year" label="প্রস্তুতকাল" value={f.year} onChange={set('year')} data-id="element-1244" data-mp="2kgr6am7yqa0b" />
              {type === 'motorcycle' && <Field id="f-cc" label="ইঞ্জিন ক্ষমতা (CC)" value={f.extra1} onChange={set('extra1')} required data-id="element-1245" data-mp="386bsrf3brpec" />}
              {type === 'auto' && <Field id="f-bat" label="ব্যাটারি (সংখ্যা × ক্ষমতা)" value={f.extra1} onChange={set('extra1')} required data-id="element-1246" data-mp="1cvty15u3i668" />}
              {type === 'auto' && <Field id="f-mtr" label="মোটর ক্ষমতা" value={f.extra2} onChange={set('extra2')} data-id="element-1247" data-mp="184lp0awogmtl" />}
              {type === 'auto' && <NativeSelect id="f-bt" label="ব্যাটারির ধরন" options={['লেড-এসিড', 'লিথিয়াম-আয়ন']} data-id="element-1248" data-mp="136k5xpz94ni4" />}
              {type === 'cng' && <Field id="f-cyl" label="সিএনজি সিলিন্ডার নং" value={f.extra1} onChange={set('extra1')} required mono data-id="element-1249" data-mp="z2vwf1r27hu8" />}
              {type === 'cng' && <Field id="f-cylx" label="সিলিন্ডার পরীক্ষার মেয়াদ" value={f.extra2} onChange={set('extra2')} required data-id="element-1250" data-mp="1qvmwjnbv5qor" />}
            </div>
          </Panel>

          <Panel title={type === 'auto' ? 'নিবন্ধন / লাইসেন্স তথ্য' : 'BRTA তথ্য'} subtitle={type === 'auto' ? 'City Corporation License' : 'BRTA Information'} data-id="element-1251" data-mp="1mwe0ucx9jiqk">
            <div className="grid gap-4 md:grid-cols-4" data-id="element-1252" data-mp="bfw74kl1vu7m">
              {type === 'auto' && <NativeSelect id="f-auth" label="লাইসেন্স প্রদানকারী কর্তৃপক্ষ" options={['ঢাকা উত্তর সিটি কর্পোরেশন', 'ঢাকা দক্ষিণ সিটি কর্পোরেশন', 'BRTA']} data-id="element-1253" data-mp="6fs0dm9y2dul" />}
              <Field id="f-rd" label={type === 'auto' ? 'লাইসেন্স ইস্যুর তারিখ' : 'নিবন্ধনের তারিখ'} value={f.regDate} onChange={set('regDate')} required data-id="element-1254" data-mp="2i1k35zvosaex" />
              {type !== 'motorcycle' && <Field id="f-fit" label={type === 'auto' ? 'লাইসেন্স মেয়াদ' : 'ফিটনেস মেয়াদ'} value={f.fitnessExpiry} onChange={set('fitnessExpiry')} required data-id="element-1255" data-mp="2r2rzwdik136w" />}
              {type !== 'auto' && <NativeSelect id="f-tax" label="ট্যাক্স টোকেন" options={['বৈধ', 'মেয়াদোত্তীর্ণ']} data-id="element-1256" data-mp="30t35s5lstmgf" />}
              {type === 'motorcycle' && <Field id="f-ins" label="বীমা পলিসি নং" value={f.extra2} onChange={set('extra2')} mono data-id="element-1257" data-mp="35v2abit9ypey" />}
              <NativeSelect id="f-bs" label={`${cfg.authority} স্ট্যাটাস`} options={['যাচাইকৃত', 'অপেক্ষমাণ', 'অমিল']} data-id="element-1258" data-mp="3apy5grtt5swa" />
              {type === 'cng' && <Field id="f-route" label="রুট পারমিট" value={f.extra3} onChange={set('extra3')} data-id="element-1259" data-mp="1gejey8sz3pfh" />}
              {type === 'auto' && <div className="md:col-span-2" data-id="element-1260" data-mp="1kgo5uzs1e7yf"><Field id="f-area" label="অনুমোদিত চলাচল এলাকা / রুট" value={f.extra3} onChange={set('extra3')} required hint="প্রধান সড়কে চলাচল নিষিদ্ধ — শুধু অনুমোদিত এলাকা" data-id="element-1261" data-mp="1kfwfq4h7l2fj" /></div>}
              {type === 'motorcycle' && <NativeSelect id="f-ride" label="রাইড শেয়ারিং" options={['পাঠাও', 'উবার মটো', 'অন্যান্য', 'ব্যবহার করে না']} value={f.extra3} onChange={e => set('extra3')(e.target.value)} data-id="element-1262" data-mp="3ujb2vg8xhqve" />}
            </div>
          </Panel>

          <Panel title="মালিকের তথ্য" subtitle="Owner Information" data-id="element-1263" data-mp="d9mirxt4ix0t">
            <div className="grid gap-4 md:grid-cols-3" data-id="element-1264" data-mp="d79xbao5q4j0">
              <Field id="f-on" label="নাম" value={f.ownerName} onChange={set('ownerName')} required data-id="element-1265" data-mp="h9wmt4xcvdd1" />
              <Field id="f-nid" label="NID" value={f.nid} onChange={set('nid')} required mono error={errors.nid} data-id="element-1266" data-mp="m2pspw4k8rqp" />
              <Field id="f-mob" label="মোবাইল" value={f.mobile} onChange={set('mobile')} required mono data-id="element-1267" data-mp="r09fxlj8keus" />
              <div className="md:col-span-2" data-id="element-1268" data-mp="w1p07bdlahc4"><Field id="f-addr" label="ঠিকানা" value={f.address} onChange={set('address')} data-id="element-1269" data-mp="10yymwj9lgew9" /></div>
              <NativeSelect id="f-div" label="ট্রাফিক বিভাগ" options={trafficDivisions} defaultValue={existing?.division ?? 'মিরপুর'} data-id="element-1270" data-mp="2lgzo9bk7743c" />
            </div>
            {type === 'motorcycle' && <label className="mt-4 flex items-center gap-2 rounded-md border border-border bg-slate-50 px-3 py-2.5 text-sm text-navy-900" data-id="element-1271" data-mp="2gk970kwnq2gc">
                <input type="checkbox" checked={ownerDrives} onChange={e => setOwnerDrives(e.target.checked)} className="accent-navy-800" data-id="element-1272" data-mp="26ks76k436isa" />
                মালিক নিজেই চালক — মালিকের লাইসেন্স দিয়ে চালক QR কার্ড তৈরি হবে
              </label>}
          </Panel>

          <div className="flex justify-end gap-2 rounded-lg border border-border bg-white p-4" data-id="element-1273" data-mp="2uhe9kax5wj6u">
            <Button variant="outline" onClick={() => toast.success('খসড়া সংরক্ষণ করা হয়েছে')} data-id="element-1274" data-mp="2piumu8aqv3df"><Save className="h-4 w-4" data-id="element-1275" data-mp="3mxysy91n8gnd" />খসড়া সংরক্ষণ</Button>
            <Button onClick={submit} data-id="element-1276" data-mp="3mzjoa0vs83ma"><Send className="h-4 w-4" data-id="element-1277" data-mp="3n01pzd17ra1l" />যাচাইয়ের জন্য পাঠান</Button>
          </div>
        </div>

        <aside className="space-y-4" data-id="element-1278" data-mp="3i55pdohwvecv">
          <Panel title="ডকুমেন্ট আপলোড" subtitle={cfg.label} data-id="element-1279" data-mp="1g96mvfqv2ahh">
            <ul className="space-y-2 text-sm" data-id="element-1280" data-mp="1gc2i2wbbql28">
              {cfg.documents.map((d, i) => <li key={d} className="flex items-center justify-between gap-2 rounded border border-border px-3 py-2" data-id="element-1281" data-mp="1lavusmise81c" data-mpi={i}>
                  <span data-id="element-1282" data-mp="1bizv5711y9v0">{d}</span>
                  {i < 2 ? <span className="shrink-0 text-xs font-semibold text-emerald-700" data-id="element-1283" data-mp="1bhouvh6tfk8n">✓ সংযুক্ত</span> : <button className="shrink-0 text-xs font-semibold text-navy-600" data-id="element-1284" data-mp="xiyv1tsm36gl">আপলোড</button>}
                </li>)}
            </ul>
          </Panel>
          <div className="rounded-lg border border-border bg-white p-4 text-xs" data-id="element-1285" data-mp="nl10ypo2mfg2">
            <p className="text-slate-500" data-id="element-1286" data-mp="nn59xm0tu4n7">অনুমোদনের পর তৈরি হবে</p>
            <p className="mt-1 font-mono text-base font-bold text-navy-900" data-id="element-1287" data-mp="skx4knsuw4v3">{cfg.qrPrefix}XXXXXX</p>
            <p className="mt-1 text-slate-600" data-id="element-1288" data-mp="2i0d91pvu52en">যানবাহন QR স্টিকার + প্রত্যেক {cfg.driverLabel}ের জন্য DRV- কার্ড</p>
          </div>
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-xs leading-relaxed text-blue-800" data-id="element-1289" data-mp="2mvsnok1o32c4">
            জমা দেওয়ার পর রেকর্ডটি <b data-id="element-1290" data-mp="6chwd7pfchyo">তথ্য যাচাই</b> ধাপে যাবে। যাচাইকারী {cfg.authority} তথ্যের সাথে মিলিয়ে অনুমোদনের জন্য পাঠাবেন।
          </div>
        </aside>
      </div>
    </div>;
}